import { RowSelectionState } from '@tanstack/react-table'
import FbButton from 'components/FbUI/FbButton'
import { Download, Save } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PATHS } from 'paths'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { FbModal } from 'components/UI/Modal'
import { DebugTable } from './DebugTable'
import { useImporterViewModel } from '../ImporterViewModel'
import { ImportWizardConfirmMatchedStep } from './ConfirmMatchedStep'
import {
  ImportWizardFinishMatchingStep,
  CustomCompanyType,
} from './FinishMatchingStep'
import { ImportWizardDialogConfirmDeselectMatched } from './DialogConfirmDeselectMatched'
import { ImportWizardReviewPendingStep } from './ReviewPendingStep'
import { ImportWizardStartImportStep } from './StartImportStep'
import { CSVRow, ImportData, InternalColumns } from '../importer-types'
import { WizardStep, ProcessType } from './types'
import { useWizard } from '../Importer'
import {
  setIsCustomCompanyByHash,
  setCustomCompanyCategoryByHash,
  setReadyByHash,
  setMatchedChainByHash,
  setMatchedDoorByHash,
} from '../importDataTransforms'

export function ImportWizard() {
  const viewModel = useImporterViewModel()
  const wizard = useWizard()
  const navigate = useNavigate()

  // ================= UI State ======================
  const [showDebug, setShowDebug] = useState(false)

  // Step 1: Track which rows user wants to deselect (local UI state)
  const [deselectedRows, setDeselectedRows] = useState<RowSelectionState>({})

  // Step 3: Unmatched action preferences
  const [unmatchedAction, setUnmatchedAction] = useState<'create' | 'exclude'>(
    'exclude'
  )
  const [customCompanyType, setCustomCompanyType] =
    useState<CustomCompanyType>(null)

  // ================= Initialize customCompanyType ======================
  useEffect(() => {
    if (!customCompanyType && viewModel.otherOtherType) {
      setCustomCompanyType({
        category_id: viewModel.otherOtherType.category_id,
        subcategory_id: viewModel.otherOtherType.value,
      })
    }
  }, [viewModel.otherOtherType, customCompanyType])

  // ================= Reset state on data refresh ======================
  const prevDataVersion = useRef(wizard.dataVersion)
  useEffect(() => {
    if (wizard.dataVersion !== prevDataVersion.current) {
      setDeselectedRows({})
      setUnmatchedAction('exclude')
      // Keep customCompanyType as it's a preference
      prevDataVersion.current = wizard.dataVersion
    }
  }, [wizard.dataVersion])

  // ================= Derived Values ======================
  const rowsToBeRemovedFromReadyState = useMemo<CSVRow[]>(() => {
    return (
      viewModel.companiesPrematched.filter(
        (row: CSVRow) => deselectedRows[row[InternalColumns.COMPANY_HASH]]
      ) ?? []
    )
  }, [deselectedRows, viewModel.companiesPrematched])

  const hasDeselectedRows = Object.values(deselectedRows).some(
    (v) => v === true
  )

  // ================= Step 1 Handlers ======================
  const handleRemoveFromReadyState = useCallback(async () => {
    const deselectedRowCompanyHashes = rowsToBeRemovedFromReadyState.map(
      (row) => row[InternalColumns.COMPANY_HASH]
    )

    if (deselectedRowCompanyHashes.length > 0) {
      // Use pure transforms: set ready = false, clear matched chain/door
      const newData = viewModel.updateImportData((data) => {
        let result = setReadyByHash(data, deselectedRowCompanyHashes, false)
        for (const companyHash of deselectedRowCompanyHashes) {
          result = setMatchedChainByHash(result, companyHash, null, false)
          result = setMatchedDoorByHash(result, companyHash, null, false)
        }
        return result
      })

      setDeselectedRows({})

      if (newData) {
        await viewModel.handleSave(newData)
      }
    } else {
      setDeselectedRows({})
    }
  }, [rowsToBeRemovedFromReadyState, viewModel])

  const handleNextFromStep1 = useCallback(() => {
    if (hasDeselectedRows) {
      wizard.openDeselectModal()
    } else {
      wizard.goNext()
    }
  }, [hasDeselectedRows, wizard])

  const handleConfirmDeselectAndNavigate = useCallback(async () => {
    await handleRemoveFromReadyState()
    wizard.confirmDeselect()
  }, [handleRemoveFromReadyState, wizard])

  // ================= Step 3 Handlers ======================
  /**
   * Prepares import data for the set-companies process.
   *
   * Uses pure transform functions to apply mutations immutably, then returns
   * the new ImportData. The transform functions create new row objects,
   * never mutating in place.
   *
   * Returns the importData to pass to the machine, or null if validation fails.
   */
  const prepareConfirmMatches = useCallback(
    (mode: 'normal' | 'skip' = 'normal'): ImportData | null => {
      if (!viewModel.importData) return null

      // Determine which companies to process based on mode
      const companiesToProcess =
        mode === 'skip'
          ? viewModel.allCompanies
          : viewModel.companiesLeftUnmatched

      // Build the transform function that will be applied
      const newData = viewModel.updateImportData((data) => {
        let result = data

        if (companiesToProcess.length > 0) {
          const hashes = companiesToProcess.map(
            (row: CSVRow) => row[InternalColumns.COMPANY_HASH]
          )

          // In skip mode, always 'create' (never 'exclude')
          const shouldCreate = mode === 'skip' || unmatchedAction === 'create'

          if (shouldCreate) {
            const categoryId =
              customCompanyType?.category_id ??
              viewModel.otherOtherType?.category_id
            const subcategoryId =
              customCompanyType?.subcategory_id ??
              viewModel.otherOtherType?.value

            if (!categoryId || !subcategoryId) {
              // Return original data - validation will fail below
              return data
            }

            result = setIsCustomCompanyByHash(result, hashes, true)
            result = setCustomCompanyCategoryByHash(
              result,
              hashes,
              categoryId,
              subcategoryId
            )
          } else if (unmatchedAction === 'exclude') {
            result = setIsCustomCompanyByHash(result, hashes, false)
          }
        }

        // Set reviewed companies to ready = true
        // In skip mode, mark all companies as ready
        // In normal mode, only mark companiesPendingReview that have matches or are custom
        // Note: We need to use the result rows to check IS_CUSTOM_COMPANY since we may have just set it
        const companiesToReady =
          mode === 'skip'
            ? viewModel.allCompanies
            : viewModel.companiesPendingReview.filter((row: CSVRow) => {
                // Find the corresponding row in result to check updated values
                const updatedRow = result.rows.find(
                  (r) =>
                    r[InternalColumns.COMPANY_HASH] ===
                    row[InternalColumns.COMPANY_HASH]
                )
                return (
                  updatedRow?.[InternalColumns.MATCHED_CHAIN] ||
                  updatedRow?.[InternalColumns.IS_CUSTOM_COMPANY]
                )
              })

        if (companiesToReady.length > 0) {
          const readyHashes = companiesToReady.map(
            (row: CSVRow) => row[InternalColumns.COMPANY_HASH]
          )
          result = setReadyByHash(result, readyHashes, true)
        }

        return result
      })

      // Validation: check if company type was required but missing
      const categoryId =
        customCompanyType?.category_id ?? viewModel.otherOtherType?.category_id
      const subcategoryId =
        customCompanyType?.subcategory_id ?? viewModel.otherOtherType?.value
      const shouldCreate = mode === 'skip' || unmatchedAction === 'create'

      if (shouldCreate && companiesToProcess.length > 0) {
        if (!categoryId || !subcategoryId) {
          toast.error('Please select a company type for custom companies.')
          return null
        }
      }

      if (!newData) return null

      // Asserts to guarantee consistency (only in normal mode)
      if (mode !== 'skip') {
        if (
          newData.rows.some(
            (r: CSVRow) =>
              !r[InternalColumns.MATCHED_CHAIN] &&
              !r[InternalColumns.IS_CUSTOM_COMPANY] &&
              r[InternalColumns.READY]
          )
        ) {
          throw new Error('[IE1] Internal Error. Contact Support.')
        }
        if (
          newData.rows.some(
            (r: CSVRow) =>
              (r[InternalColumns.MATCHED_CHAIN] ||
                r[InternalColumns.IS_CUSTOM_COMPANY]) &&
              !r[InternalColumns.READY]
          )
        ) {
          const debugInconsistentRows = newData.rows.filter(
            (r: CSVRow) =>
              (r[InternalColumns.MATCHED_CHAIN] ||
                r[InternalColumns.IS_CUSTOM_COMPANY]) &&
              !r[InternalColumns.READY]
          )
          console.warn(debugInconsistentRows)
          throw new Error('[IE2] Internal Error. Contact Support.')
        }
      }

      return newData
    },
    [viewModel, unmatchedAction, customCompanyType]
  )

  const handleNextFromStep3 = useCallback(() => {
    if (viewModel.skipMatching) {
      // Skip matching flow - prepare data and go directly to processing
      try {
        const data = prepareConfirmMatches('skip')
        if (!data) return // Validation failed (e.g., no company type selected)
        // Machine handles: save data → call set-companies API → poll → refresh
        wizard.startProcess(ProcessType.SETTING_COMPANIES, undefined, data)
      } catch (e) {
        console.error(e)
        toast.error(String(e))
      }
      return
    }

    // Normal flow - open confirmation modal
    wizard.openConfirmModal()
  }, [viewModel.skipMatching, prepareConfirmMatches, wizard])

  const handleConfirmMatchesFromModal = useCallback(() => {
    try {
      const data = prepareConfirmMatches('normal')
      if (!data) return // Validation failed
      // Machine handles: save data → call set-companies API → poll → refresh
      wizard.startProcess(ProcessType.SETTING_COMPANIES, undefined, data)
    } catch (e) {
      console.error(e)
      toast.error(String(e))
      wizard.cancelConfirm()
    }
  }, [prepareConfirmMatches, wizard])

  // ================= Step 4 Handlers ======================
  // Machine handles the full lifecycle: call API → poll → refresh.
  // Component only sends a single event. For deals, pre-flight validation
  // (which may open modals) still runs in the component.
  const handleImportTags = useCallback(() => {
    wizard.startProcess(ProcessType.SETTING_TAGS, WizardStep.STEP4_IMPORT)
  }, [wizard])

  const handleImportDeals = useCallback(async () => {
    try {
      const isValid = await viewModel.validateDeals()
      if (!isValid) return // Validation opened a modal for user input
      wizard.startProcess(ProcessType.SETTING_DEALS, WizardStep.STEP4_IMPORT)
    } catch (error) {
      console.error('Import deals validation error:', error)
      toast.error('An error occurred while validating deals')
    }
  }, [viewModel, wizard])

  const handleImportContacts = useCallback(() => {
    wizard.startProcess(ProcessType.SETTING_CONTACTS, WizardStep.STEP4_IMPORT)
  }, [wizard])

  const handleImportNotes = useCallback(() => {
    wizard.startProcess(
      ProcessType.SETTING_COMPANY_NOTES,
      WizardStep.STEP4_IMPORT
    )
  }, [wizard])

  // ================= Save Handler ======================
  // Marks matched companies as READY and saves using the machine's unified save flow
  const handleSaveClick = useCallback(() => {
    if (!viewModel.importData) return

    // Get companies with matches that aren't ready yet
    const companiesWithMatches = viewModel.importData.rows.filter(
      (row: CSVRow) =>
        (row[InternalColumns.MATCHED_CHAIN] ||
          row[InternalColumns.MATCHED_DOOR]) &&
        !row[InternalColumns.READY]
    )

    // Mark matched companies as READY using pure transform
    let dataToSave: ImportData = viewModel.importData
    if (companiesWithMatches.length > 0) {
      const hashes = [
        ...new Set(
          companiesWithMatches.map(
            (r: CSVRow) => r[InternalColumns.COMPANY_HASH]
          )
        ),
      ]
      const newData = viewModel.updateImportData((data) =>
        setReadyByHash(data, hashes, true)
      )
      if (newData) {
        dataToSave = newData
      }
    }

    // Use the machine's save with the updated data
    wizard.saveAndReload(dataToSave, viewModel.skipMatching)
  }, [viewModel, wizard])

  // ================= Generic Next Handler ======================
  const handleNext = useCallback(() => {
    switch (wizard.currentStep) {
      case WizardStep.STEP1_CONFIRM:
        handleNextFromStep1()
        break
      case WizardStep.STEP2_REVIEW:
        wizard.goNext()
        break
      case WizardStep.STEP3_DECIDE:
        handleNextFromStep3()
        break
      default:
        wizard.goNext()
    }
  }, [wizard, handleNextFromStep1, handleNextFromStep3])

  // ================= Prevent navigation when importing ======================
  useEffect(() => {
    const isImporting = wizard.isProcessing || wizard.isWaitingForProcess

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isImporting) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    const handlePopState = (e: PopStateEvent) => {
      if (isImporting) {
        e.preventDefault()
        const confirmLeave = window.confirm(
          'Import is in progress. Are you sure you want to leave? This may cause data loss.'
        )
        if (!confirmLeave) {
          window.history.pushState(null, '', window.location.href)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    // Push state to enable popstate detection
    if (isImporting) {
      window.history.pushState(null, '', window.location.href)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [wizard.isProcessing, wizard.isWaitingForProcess])

  // ================= Loading State ======================
  // Loading comes entirely from the machine — no local loading state needed
  const isShowingLoading =
    wizard.isLoading ||
    wizard.isProcessing ||
    wizard.isWaitingForProcess ||
    wizard.isSaving

  const loadingDisplayMessage =
    wizard.loadingMessage || 'Loading import data...'

  // ================= Render ======================
  return (
    <div className="flex flex-col overflow-hidden position-relative w-full">
      {/* Loading Overlay */}
      {isShowingLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-fb-dark-green" />
            <div className="text-lg font-semibold text-gray-900">
              {loadingDisplayMessage}
            </div>
            <div className="text-sm text-gray-600">
              Please do not close this window
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b pb-2 mb-2">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {wizard.currentStep === WizardStep.STEP3_DECIDE &&
                viewModel.skipMatching
                  ? 'Create Custom Companies'
                  : wizard.currentStep && stepTitles[wizard.currentStep].title}
              </h2>
              {wizard.importName && (
                <span className="text-sm text-gray-500 font-normal">
                  — {wizard.importName}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {wizard.currentStep === WizardStep.STEP3_DECIDE &&
              viewModel.skipMatching
                ? 'All companies will be created as custom (user-generated) companies. Select the company type category and review before proceeding.'
                : wizard.currentStep &&
                  stepTitles[wizard.currentStep].description}
            </p>
          </div>
          <div className="flex gap-2">
            <FbButton
              type="button"
              onClick={handleSaveClick}
              variant="primary"
              disabled={!viewModel.hasPendingChanges || wizard.isSaving}
              loading={wizard.isSaving}
            >
              <Save size={16} />
              Save
            </FbButton>
            <FbButton
              type="button"
              onClick={() => viewModel.setShowConfigureExportModal(true)}
              variant="secondary"
            >
              <Download size={16} />
              Export
            </FbButton>
            <FbButton
              type="button"
              onClick={() => setShowDebug(!showDebug)}
              variant="secondary"
              className="hidden"
            >
              {showDebug ? 'Hide Debug' : 'Show Debug'}
            </FbButton>
            <FbButton
              variant="secondary"
              onClick={() => navigate(PATHS.IMPORT_WIZARD)}
            >
              Go Back to List
            </FbButton>
          </div>
        </div>
      </div>

      {/* Content */}
      {showDebug ? (
        <DebugTable data={viewModel.importData?.rows ?? []} />
      ) : (
        <>
          {/* Step 1: Confirm Matched */}
          {wizard.matches(WizardStep.STEP1_CONFIRM) && (
            <ImportWizardConfirmMatchedStep
              loading={wizard.isLoading}
              show={!isShowingLoading}
              rowSelection={deselectedRows}
              setRowSelection={setDeselectedRows}
              headers={viewModel.wizardHeaders}
              data={viewModel.companiesPrematched}
            />
          )}

          {/* Step 1 Modal: Deselect Confirmation */}
          <ImportWizardDialogConfirmDeselectMatched
            show={wizard.isDeselectModalOpen && !isShowingLoading}
            headers={viewModel.wizardHeaders}
            data={rowsToBeRemovedFromReadyState}
            onCancel={wizard.cancelDeselect}
            onAccept={handleConfirmDeselectAndNavigate}
          />

          {/* Step 2: Review Pending */}
          {wizard.matches(WizardStep.STEP2_REVIEW) && (
            <ImportWizardReviewPendingStep
              show={!isShowingLoading}
              data={viewModel.companiesPendingReview}
            />
          )}

          {/* Step 3: Decide Unmatched */}
          {wizard.matches(WizardStep.STEP3_DECIDE) && (
            <ImportWizardFinishMatchingStep
              show={!isShowingLoading}
              data={viewModel.allCompanies}
              selectedAction={unmatchedAction}
              onActionChange={setUnmatchedAction}
              selectedCustomCompanyType={customCompanyType}
              onCustomCompanyTypeChange={setCustomCompanyType}
              skipMatching={viewModel.skipMatching}
            />
          )}

          {/* Step 3 Modal: Confirm Matches */}
          <ConfirmMatchesModal
            show={wizard.isConfirmModalOpen}
            onCancel={wizard.cancelConfirm}
            onConfirm={handleConfirmMatchesFromModal}
            unmatchedAction={unmatchedAction}
            amountCompaniesLeftUnmatched={
              viewModel.amountCompaniesLeftUnmatched
            }
          />

          {/* Step 4: Import */}
          {wizard.matches(WizardStep.STEP4_IMPORT) && (
            <ImportWizardStartImportStep
              show={!isShowingLoading}
              onImportTags={handleImportTags}
              onImportDeals={handleImportDeals}
              onImportContacts={handleImportContacts}
              onImportNotes={handleImportNotes}
              isProcessing={wizard.isProcessing}
              currentProcess={wizard.currentProcess}
            />
          )}
        </>
      )}

      {/* Footer with stats and buttons */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-4 text-sm text-gray-600">
            <div>
              Matched:{' '}
              {viewModel.allCompanies.length -
                viewModel.amountCompaniesLeftUnmatched}
            </div>
            <div>Unmatched: {viewModel.amountCompaniesLeftUnmatched}</div>
          </div>

          <div className="flex gap-2">
            {wizard.canGoBack &&
              !(
                wizard.currentStep === WizardStep.STEP4_IMPORT &&
                viewModel.areAllRowsSet
              ) && (
                <FbButton
                  type="button"
                  onClick={wizard.goBack}
                  variant="secondary"
                >
                  {wizard.currentStep &&
                    stepButtonTexts[wizard.currentStep].back}
                </FbButton>
              )}
            {wizard.currentStep &&
              wizard.currentStep !== WizardStep.STEP4_IMPORT && (
                <FbButton type="button" onClick={() => handleNext()}>
                  {wizard.currentStep === WizardStep.STEP3_DECIDE &&
                  viewModel.skipMatching
                    ? 'Create Custom Companies'
                    : wizard.currentStep &&
                      stepButtonTexts[wizard.currentStep].next}
                </FbButton>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ================= Confirm Matches Modal ======================
function ConfirmMatchesModal({
  show,
  onCancel,
  onConfirm,
  unmatchedAction,
  amountCompaniesLeftUnmatched,
}: {
  show: boolean
  onCancel: () => void
  onConfirm: () => void
  unmatchedAction: 'create' | 'exclude'
  amountCompaniesLeftUnmatched: number
}) {
  let confirmationText =
    " By pressing 'Confirm', you confirm all the selected matches "
  if (amountCompaniesLeftUnmatched > 0 && unmatchedAction === 'create') {
    confirmationText +=
      'and agree that companies marked as unmatched will be created as user-generated chains.'
  }

  return (
    <FbModal
      open={show}
      onOpenChange={(open) => !open && onCancel()}
      title="Confirm Your Matches"
      description={confirmationText}
      cancelButtonText="Cancel"
      acceptButtonText="Confirm"
      onCancel={onCancel}
      onAccept={onConfirm}
    />
  )
}

// ================= Step Metadata ======================
const stepTitles: Record<WizardStep, { title: string; description: string }> = {
  [WizardStep.STEP1_CONFIRM]: {
    title: 'Confirm Matches',
    description:
      'These are high-confidence matches, please review and confirm. You can also deselect matches that you do not think are correct.',
  },
  [WizardStep.STEP2_REVIEW]: {
    title: 'Review Pending Matches',
    description: 'Review the pending data',
  },
  [WizardStep.STEP3_DECIDE]: {
    title: 'Confirm Matching',
    description:
      'Review the matching and confirm to move on to importing CRM data.',
  },
  [WizardStep.STEP4_IMPORT]: {
    title: 'CRM Data Import Summary',
    description:
      'Review the CRM data (Tags, Deals, Contacts, Company Notes) that will be imported',
  },
}

const stepButtonTexts: Record<WizardStep, { back: string; next: string }> = {
  [WizardStep.STEP1_CONFIRM]: { back: 'Back', next: 'Next' },
  [WizardStep.STEP2_REVIEW]: { back: 'Back', next: 'Finish Review' },
  [WizardStep.STEP3_DECIDE]: { back: 'Back', next: 'Next' },
  [WizardStep.STEP4_IMPORT]: { back: 'Back', next: 'Start Import' },
}
